import { useState, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronRight, ChevronDown, Clock, Check } from "lucide-react";
import { IBIC_CATEGORIES, IBIC_GROUPS, extractIbicCode, isGroupHeader } from "../../constants/ibicCategories";
import { useRecentlyUsedIbic } from "../../hooks/useRecentlyUsedIbic";
import styles from "./IbicDropdown.module.css";

interface IbicDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (category: string) => void;
    selectedValue?: string;
    showAllOption?: boolean;
    triggerRef?: React.RefObject<HTMLElement>;
    filterType?: "category" | "code" | "all";
    className?: string;
}

export const IbicDropdown = ({
    isOpen,
    onClose,
    onSelect,
    selectedValue,
    showAllOption = false,
    triggerRef,
    filterType = "all",
    className
}: IbicDropdownProps) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const { recentlyUsed, addRecent } = useRecentlyUsedIbic();

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                triggerRef?.current &&
                !triggerRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose, triggerRef]);

    // Reset state when dropdown closes
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            setExpandedGroup(null);
        }
    }, [isOpen]);

    // Get base categories based on filterType
    const baseCategories = useMemo(() => {
        if (filterType === "category") {
            // Only main categories (d1, d2, d3, etc.)
            return IBIC_CATEGORIES.filter(cat => isGroupHeader(cat));
        }
        if (filterType === "code") {
            // Only sub-codes (d550, d155, etc.)
            return IBIC_CATEGORIES.filter(cat => !isGroupHeader(cat));
        }
        // "all" - return everything
        return [...IBIC_CATEGORIES];
    }, [filterType]);

    // Filter recently used based on filterType
    const filteredRecentlyUsed = useMemo(() => {
        if (filterType === "category") {
            return recentlyUsed.filter(cat => isGroupHeader(cat));
        }
        if (filterType === "code") {
            return recentlyUsed.filter(cat => !isGroupHeader(cat));
        }
        return recentlyUsed;
    }, [recentlyUsed, filterType]);

    // Filter categories based on search term
    const filteredCategories = useMemo(() => {
        if (!searchTerm.trim()) return null;
        return baseCategories.filter(category =>
            category.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, baseCategories]);

    // Translate IBIC category
    const translateCategory = (category: string): string => {
        const key = `notes.ibicCategories.${category}`;
        const translated = t(key);
        return translated === key ? category : translated;
    };

    // Handle category selection
    const handleSelect = (category: string) => {
        addRecent(category);
        onSelect(category);
        onClose();
    };

    // Handle group toggle
    const toggleGroup = (groupKey: string) => {
        setExpandedGroup(prev => prev === groupKey ? null : groupKey);
    };

    // Handle "All" option selection
    const handleSelectAll = () => {
        onSelect("all");
        onClose();
    };

    if (!isOpen) return null;

    const isSearchMode = searchTerm.trim().length > 0;

    return (
        <div className={`${styles.dropdown}${className ? ` ${className}` : ""}`} ref={dropdownRef}>
            {/* Search Input */}
            <div className={styles.searchContainer}>
                <Search size={16} className={styles.searchIcon} />
                <input
                    ref={searchInputRef}
                    type="text"
                    className={styles.searchInput}
                    placeholder={t("notes.searchByIbic")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Escape") {
                            onClose();
                        }
                    }}
                />
            </div>

            <div className={styles.content}>
                {/* Show All Option for filters */}
                {showAllOption && !isSearchMode && (
                    <button
                        className={`${styles.allOption} ${selectedValue === "all" ? styles.selected : ""}`}
                        onClick={handleSelectAll}
                    >
                        {t("notes.allIbicCategories")}
                        {selectedValue === "all" && <Check size={16} className={styles.checkIcon} />}
                    </button>
                )}

                {/* Search Results Mode */}
                {isSearchMode && (
                    <div className={styles.searchResults}>
                        {filteredCategories && filteredCategories.length > 0 ? (
                            filteredCategories.map((category) => (
                                <button
                                    key={category}
                                    className={`${styles.item} ${selectedValue === category ? styles.selected : ""}`}
                                    onClick={() => handleSelect(category)}
                                >
                                    <span className={styles.itemCode}>{extractIbicCode(category)}</span>
                                    <span className={styles.itemLabel}>{translateCategory(category)}</span>
                                    {selectedValue === category && <Check size={16} className={styles.checkIcon} />}
                                </button>
                            ))
                        ) : (
                            <div className={styles.noResults}>{t("notes.noIbicFound")}</div>
                        )}
                    </div>
                )}

                {/* Accordion Mode */}
                {!isSearchMode && (
                    <>
                        {/* Recently Used Section */}
                        {filteredRecentlyUsed.length > 0 && (
                            <div className={styles.recentSection}>
                                <div className={styles.sectionHeader}>
                                    <Clock size={14} />
                                    <span>{t("notes.ibicDropdown.recentlyUsed")}</span>
                                </div>
                                {filteredRecentlyUsed.map((category) => (
                                    <button
                                        key={`recent-${category}`}
                                        className={`${styles.item} ${styles.recentItem} ${selectedValue === category ? styles.selected : ""}`}
                                        onClick={() => handleSelect(category)}
                                    >
                                        <span className={styles.itemCode}>{extractIbicCode(category)}</span>
                                        <span className={styles.itemLabel}>{translateCategory(category)}</span>
                                        {selectedValue === category && <Check size={16} className={styles.checkIcon} />}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Category Mode - Flat list of main categories */}
                        {filterType === "category" && (
                            <div className={styles.flatList}>
                                {baseCategories.map((category) => (
                                    <button
                                        key={category}
                                        className={`${styles.item} ${selectedValue === category ? styles.selected : ""}`}
                                        onClick={() => handleSelect(category)}
                                    >
                                        <span className={styles.itemCode}>{extractIbicCode(category)}</span>
                                        <span className={styles.itemLabel}>{translateCategory(category)}</span>
                                        {selectedValue === category && <Check size={16} className={styles.checkIcon} />}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Code Mode - Accordion with sub-codes only */}
                        {filterType === "code" && (
                            <div className={styles.accordionSection}>
                                {IBIC_GROUPS.map((group) => (
                                    <div key={group.key} className={styles.group}>
                                        {/* Group Header */}
                                        <button
                                            className={`${styles.groupHeader} ${expandedGroup === group.key ? styles.expanded : ""}`}
                                            onClick={() => toggleGroup(group.key)}
                                        >
                                            <div className={styles.groupHeaderLeft}>
                                                {expandedGroup === group.key ? (
                                                    <ChevronDown size={16} />
                                                ) : (
                                                    <ChevronRight size={16} />
                                                )}
                                                <span className={styles.groupCode}>{group.key}</span>
                                                <span className={styles.groupLabel}>{translateCategory(group.header)}</span>
                                            </div>
                                            <span className={styles.itemCount}>({group.items.length})</span>
                                        </button>

                                        {/* Group Items - only sub-codes */}
                                        {expandedGroup === group.key && (
                                            <div className={styles.groupItems}>
                                                {group.items.map((item) => (
                                                    <button
                                                        key={item}
                                                        className={`${styles.item} ${styles.subItem} ${selectedValue === item ? styles.selected : ""}`}
                                                        onClick={() => handleSelect(item)}
                                                    >
                                                        <span className={styles.itemCode}>{extractIbicCode(item)}</span>
                                                        <span className={styles.itemLabel}>{translateCategory(item)}</span>
                                                        {selectedValue === item && <Check size={16} className={styles.checkIcon} />}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* All Mode - Full accordion (default behavior) */}
                        {filterType === "all" && (
                            <div className={styles.accordionSection}>
                                {IBIC_GROUPS.map((group) => (
                                    <div key={group.key} className={styles.group}>
                                        {/* Group Header */}
                                        <button
                                            className={`${styles.groupHeader} ${expandedGroup === group.key ? styles.expanded : ""}`}
                                            onClick={() => toggleGroup(group.key)}
                                        >
                                            <div className={styles.groupHeaderLeft}>
                                                {expandedGroup === group.key ? (
                                                    <ChevronDown size={16} />
                                                ) : (
                                                    <ChevronRight size={16} />
                                                )}
                                                <span className={styles.groupCode}>{group.key}</span>
                                                <span className={styles.groupLabel}>{translateCategory(group.header)}</span>
                                            </div>
                                            <span className={styles.itemCount}>({group.items.length})</span>
                                        </button>

                                        {/* Group Items */}
                                        {expandedGroup === group.key && (
                                            <div className={styles.groupItems}>
                                                {group.items.map((item) => (
                                                    <button
                                                        key={item}
                                                        className={`${styles.item} ${styles.subItem} ${selectedValue === item ? styles.selected : ""}`}
                                                        onClick={() => handleSelect(item)}
                                                    >
                                                        <span className={styles.itemCode}>{extractIbicCode(item)}</span>
                                                        <span className={styles.itemLabel}>{translateCategory(item)}</span>
                                                        {selectedValue === item && <Check size={16} className={styles.checkIcon} />}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
